import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';

import { parseNullableValue } from '@hrlogics/helper-utils';
import { SxProp } from '@hrlogics/react-helper-types';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, numberWithCommas } from '@clearverify/utils';

import { useVerificationRequestsCountQuery, useVerifierCredentialInfoQuery } from 'src/api';

type VerifierBasicInfoProps = {
  verifierId?: string;
};

export const VerifierBasicInfo: React.FC<VerifierBasicInfoProps> = ({ verifierId }) => {
  const { data, loading } = useVerifierCredentialInfoQuery({
    variables: {
      where: { id: verifierId },
    },
    skip: !verifierId,
  });

  const { data: verificationRequestsCountData, loading: verificationRequestsCountDataLoading } =
    useVerificationRequestsCountQuery({
      variables: {
        where: {
          verifierStaff: {
            verifierId: { equals: verifierId },
          },
        },
      },
      skip: !verifierId,
    });

  const { createdAtDate, credentialedAt } = React.useMemo(() => {
    const { createdAt, credentialedAt } = data?.verifier || {};

    return {
      createdAtDate: parseNullableValue(
        formatDate(createdAt, DateFormatPatterns.shortDateWithSlash),
      ),
      credentialedAt: parseNullableValue(
        formatDate(credentialedAt, DateFormatPatterns.shortDateWithSlash),
        'Pending',
      ),
    };
  }, [data?.verifier]);

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Typography variant="body2" sx={typographyStyles}>
        {loading ? <Skeleton /> : <React.Fragment>Created at: {createdAtDate}</React.Fragment>}
      </Typography>

      <Typography variant="body2" sx={typographyStyles}>
        {loading ? <Skeleton /> : <React.Fragment>Certified at: {credentialedAt}</React.Fragment>}
      </Typography>

      <Typography variant="body2" sx={typographyStyles}>
        {verificationRequestsCountDataLoading ? (
          <Skeleton />
        ) : (
          <React.Fragment>
            Verifications created:{' '}
            {numberWithCommas(verificationRequestsCountData?.verificationRequestCount)}
          </React.Fragment>
        )}
      </Typography>
    </Box>
  );
};

const typographyStyles: SxProp = theme => ({
  color: theme.palette.text.secondary,
});
